import { type KeyboardEvent, useRef } from 'react';

export type ChildTab = 'agent' | 'result' | 'sitrep' | 'map' | 'children';

export interface TabHint {
	tone: 'success' | 'warning' | 'danger' | 'neutral';
	count?: number;
	locked?: boolean;
	live?: boolean;
}

export interface IChildAgentTab {
	key: ChildTab;
	label: string;
	short: string;
	hint?: TabHint;
}

interface IChildAgentTabsProps {
	tabs: IChildAgentTab[];
	active: ChildTab;
	onChange: (tab: ChildTab) => void;
}

const HintBadge = ({ hint }: { hint: TabHint }) => {
	if (hint.locked) {
		return (
			<span
				className='ca-tabs__hint ca-tabs__hint--lock'
				aria-label='not yet enabled'
			>
				<svg
					viewBox='0 0 16 16'
					fill='none'
					aria-hidden='true'
				>
					<rect
						x='3.5'
						y='7'
						width='9'
						height='6.5'
						rx='1.5'
						stroke='currentColor'
						strokeWidth='1.4'
					/>
					<path
						d='M5.5 7V5.5a2.5 2.5 0 0 1 5 0V7'
						stroke='currentColor'
						strokeWidth='1.4'
					/>
				</svg>
			</span>
		);
	}
	if (typeof hint.count === 'number') {
		return <span className={`ca-tabs__hint ca-tabs__hint--count ca-tabs__hint--${hint.tone}`}>{hint.count}</span>;
	}
	return (
		<span
			className={`ca-tabs__hint ca-tabs__hint--dot ca-tabs__hint--${hint.tone} ${hint.live ? 'is-live' : ''}`}
			aria-hidden='true'
		/>
	);
};

/**
 * Sticky, horizontally-scrollable tab-bar for the Agent Coverage drawer. Full
 * ARIA tablist semantics with roving focus + arrow/Home/End navigation. Per-tab
 * status hints (dot / count / lock) are data-driven by the parent.
 */
const ChildAgentTabs = ({ tabs, active, onChange }: IChildAgentTabsProps) => {
	const refs = useRef<(HTMLButtonElement | null)[]>([]);

	const focusTab = (index: number) => {
		const next = (index + tabs.length) % tabs.length;
		refs.current[next]?.focus();
		onChange(tabs[next].key);
	};

	const onKeyDown = (e: KeyboardEvent, index: number) => {
		switch (e.key) {
			case 'ArrowRight':
			case 'ArrowDown':
				e.preventDefault();
				focusTab(index + 1);
				break;
			case 'ArrowLeft':
			case 'ArrowUp':
				e.preventDefault();
				focusTab(index - 1);
				break;
			case 'Home':
				e.preventDefault();
				focusTab(0);
				break;
			case 'End':
				e.preventDefault();
				focusTab(tabs.length - 1);
				break;
			default:
				break;
		}
	};

	return (
		<div
			className='ca-tabs'
			role='tablist'
			aria-label='Agent coverage views'
		>
			{tabs.map((tab, index) => {
				const isActive = tab.key === active;
				return (
					<button
						key={tab.key}
						ref={(el) => (refs.current[index] = el)}
						type='button'
						role='tab'
						id={`ca-tab-${tab.key}`}
						aria-selected={isActive}
						aria-controls={`ca-panel-${tab.key}`}
						tabIndex={isActive ? 0 : -1}
						className={`ca-tabs__tab ${isActive ? 'is-active' : ''}`}
						onClick={() => onChange(tab.key)}
						onKeyDown={(e) => onKeyDown(e, index)}
					>
						<span className='ca-tabs__label'>
							<span className='ca-tabs__label-full'>{tab.label}</span>
							<span className='ca-tabs__label-short'>{tab.short}</span>
						</span>
						{tab.hint ? <HintBadge hint={tab.hint} /> : null}
					</button>
				);
			})}
		</div>
	);
};

export default ChildAgentTabs;
